import {
  APIEntityType,
  BooleanOperator,
  QueryBuilderRuleValue,
  Operator,
} from './apiTypes';

export enum SearchContext {
  COMPONENT = 'component',
  REFERENCE = 'reference',
  WORKSPACE = 'workspace',
}

type ContextRule<T extends SearchContext> = {
  value: T;
  field: 'type';
  id: 'type';
  input: 'select';
  operator: Operator.EQUAL;
  type: 'string';
};

type ComponentSearch = ContextRule<SearchContext.COMPONENT>;
type ReferenceSearch = ContextRule<SearchContext.REFERENCE>;
type WorkspaceSearch = ContextRule<SearchContext.WORKSPACE>;

export type SearchContextRule =
  | ComponentSearch
  | ReferenceSearch
  | WorkspaceSearch;

/**
 * The top level query object for the query builder.
 * - The first rule stores information if the entire query is meant to be for
 *   components or references.
 * - The second rules stores sub-queries that represent the user-defined
 *   filtering rules.
 */
export type QueryBuilderQuery = {
  condition: BooleanOperator;
  rules: [SearchContextRule, QueryBuilderSubquery];
};

/**
 * Represent nesting queries
 */
export type QueryBuilderSubquery = {
  condition: BooleanOperator;
  rules: QueryBuilderRow[];
};

export type QueryBuilderRow = QueryBuilderRule | QueryBuilderSubquery;

export type GenericQueryBuilderRule = {
  id: string;
  field: string;
  operator: Operator;
  /**
   * This type can be one of: 'attribute' | 'tag'
   * string was only left because 260 errors follow its removal
   */
  type: 'attribute' | 'tag' | string;
  input: string;
  value: QueryBuilderRuleValue;
  targetEntityType?: APIEntityType.COMPONENT_TYPE | APIEntityType.COMPONENT;
};

export type QueryBuilderRule = GenericQueryBuilderRule | HasReferenceFilterRule;

export enum ReferenceFilterIds {
  HAS_REFERENCE = 'has-reference',
  HAS_NO_REFERENCE = 'has-no-reference',
}

export type HasReferenceFilterRule = GenericQueryBuilderRule & {
  id: ReferenceFilterIds.HAS_REFERENCE;
  field: ReferenceFilterIds.HAS_REFERENCE;
  operator: Operator.TO | Operator.FROM;
  targetEntityType?: APIEntityType.COMPONENT | APIEntityType.COMPONENT_TYPE;
  referenceTypeName?: string;
};

export const isHasReferenceFilterRule = (
  rule: QueryBuilderRule
): rule is HasReferenceFilterRule =>
  rule.field === ReferenceFilterIds.HAS_REFERENCE;

export enum FieldFormattingVersion {
  ORIGINAL = 0,
  NEW_CONDITIONAL_FORMATTING = 1,
}

export enum RelativeDatePeriod {
  /** The current unit relative to now, e.g. this month. */
  THIS = 'this',
  /** The whole unit before the current one, e.g. last month. */
  LAST = 'last',
  /** The whole unit after the current one, e.g. next month. */
  NEXT = 'next',
  /** A forward window spanning `amount` units, starting now. */
  IN_THE_NEXT = 'inTheNext',
  /** A backward window spanning `amount` units, ending now. */
  IN_THE_PAST = 'inThePast',
}

export enum RelativeDateUnit {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  QUARTER = 'quarter',
  YEAR = 'year',
}

/**
 * A date expressed relative to now — a fixed period (this/last/next unit) or an
 * offset (in the next/past N units).
 */
export type RelativeDateValue =
  | {
      /** A whole period relative to now. */
      period:
        | RelativeDatePeriod.THIS
        | RelativeDatePeriod.LAST
        | RelativeDatePeriod.NEXT;
      /** The unit of time the period spans. */
      unit: RelativeDateUnit;
    }
  | {
      /** The direction of the offset window relative to now. */
      period: RelativeDatePeriod.IN_THE_NEXT | RelativeDatePeriod.IN_THE_PAST;
      /** How many `unit`s the window spans. */
      amount: number;
      /** The unit of time each step of `amount` spans. */
      unit: RelativeDateUnit;
    };

export const isRelativeDateValue = (
  value: unknown
): value is RelativeDateValue =>
  typeof value === 'object' &&
  value !== null &&
  'period' in value &&
  'unit' in value;

export const isQueryBuilderRule = (
  rule: QueryBuilderRow
): rule is QueryBuilderRule => 'field' in rule;

export const isQueryBuilderSubquery = (
  rule: QueryBuilderRow
): rule is QueryBuilderSubquery => 'condition' in rule;
